// src/pages/PublicFlyer.jsx
import {
  Box,
  Grid,
  GridItem,
  Text,
  Flex,
  Button,
  Switch,
  HStack,
  IconButton,
  Divider,
} from "@chakra-ui/react";
import { useState } from "react";
import { FiShare2 } from "react-icons/fi";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";

import AdvertSlotCard from "../components/AdvertSlotCard";
import useSectorIntelligence from "../hooks/useSectorIntelligence";
import { useSeason } from "../context/SeasonContext";

// Ensures every flyer contains 32 slots and preserves flyer_id.
function normalizeSlots(slots, flyerId) {
  const map = new Map();

  // Real slots
  slots.forEach((slot) => {
    const num = Number(slot.slot_number);
    if (!num || num < 1 || num > 32) return;

    map.set(num, {
      ...slot,
      flyer_id: slot.flyer_id ?? flyerId,
      placeholder: false,
    });
  });

  // Placeholder slots
  for (let i = 1; i <= 32; i += 1) {
    if (!map.has(i)) {
      map.set(i, {
        id: `placeholder-${i}`,
        flyer_id: flyerId,
        slot_number: i,
        status: "available",
        placeholder: true,
      });
    }
  }

  return Array.from(map.values()).sort(
    (a, b) => a.slot_number - b.slot_number
  );
}

function partitionSlots(slots) {
  return {
    left: slots.slice(0, 16),
    right: slots.slice(16, 32),
  };
}

export default function PublicFlyer({
  sector,
  slots = [],
  flyer,
  authBusiness,
  authLoading = false,
}) {
  const navigate = useNavigate();
  const { loading, error, enrichedSlots } = useSectorIntelligence(
    sector,
    slots
  );
  const { seasonLabel } = useSeason();

  const [offersOnly, setOffersOnly] = useState(false);

  if (!sector) return null;
  if (loading) return <Text>Loading postcode coupons…</Text>;
  if (error) return <Text color="red.500">Error: {error}</Text>;

  const sourceSlots =
    enrichedSlots && enrichedSlots.length > 0 ? enrichedSlots : slots;

  let normalized = normalizeSlots(sourceSlots, flyer?.id);

  if (offersOnly) {
    normalized = normalized.filter(
      (slot) => String(slot.status || "").toLowerCase() === "booked"
    );
  }

  const { left, right } = partitionSlots(normalized);

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      alert("Link copied to clipboard");
    } catch (shareError) {
      console.error("[PublicFlyer] share_error", shareError);
    }
  };

  const sectorLabel = sector.toUpperCase();
  const canonicalUrl = `https://postcode.coupons/flyer/${sector}`;

  return (
    <Box>
      <Helmet>
        <title>
          {sectorLabel} Postcode Coupons | Coupon Flyer & Local Deals
        </title>
        <meta
          name="description"
          content={`View postcode coupons and the latest Coupon Flyer for postcode sector ${sectorLabel}.`}
        />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>

      <Flex
        justify="space-between"
        mb={4}
        align={{ base: "stretch", md: "flex-start" }}
        flexDirection={{ base: "column", md: "row" }}
        gap={4}
      >
        <Box minW={0}>
          <Text fontSize="2xl" fontWeight="bold">
            {sectorLabel} postcode coupons
          </Text>

          <Text fontSize="sm" color="gray.600" maxW="600px">
            Your sector’s Coupon Flyer — {seasonLabel}.
          </Text>
        </Box>

        <Flex
          width={{ base: "100%", md: "auto" }}
          wrap="wrap"
          gap={2}
          justify={{ base: "flex-start", md: "flex-end" }}
          align="center"
        >
          <Button
            size="sm"
            variant="outline"
            flex={{ base: "1 1 auto", md: "0 0 auto" }}
            whiteSpace="nowrap"
            onClick={() => navigate(`/sector/${sector}`)}
          >
            📊 Sector
          </Button>

          <IconButton
            icon={<FiShare2 />}
            onClick={handleShare}
            colorScheme="gray"
            aria-label="Share flyer"
            title="Share flyer"
            size="sm"
            flex="0 0 auto"
          />

          <Button
            size="sm"
            colorScheme="red"
            flex={{ base: "1 1 auto", md: "0 0 auto" }}
            whiteSpace="nowrap"
            onClick={() => {
              window.open(`/print?sector=${sector}`, "_blank");
            }}
          >
            🖨️ Print
          </Button>
        </Flex>
      </Flex>

      <Divider mb={4} />

      <HStack mb={4} spacing={2}>
        <Text fontWeight="medium" fontSize="sm">
          Show booked offers only
        </Text>
        <Switch
          size="sm"
          isChecked={offersOnly}
          onChange={() => setOffersOnly((current) => !current)}
        />
      </HStack>

      <Box id="flyer-container" width="100%" mx="auto">
        <Box mb={8}>
          <Text fontWeight="bold" fontSize="lg" mb={2}>
            Front side — featured local coupons
          </Text>

          <Grid
            templateColumns="repeat(auto-fit, minmax(170px, 1fr))"
            gap={1}
          >
            {left.map((slot) => (
              <GridItem key={slot.id} minW={0}>
                <AdvertSlotCard
                  slot={slot}
                  variant="public"
                  authBusiness={authBusiness}
                  authLoading={authLoading}
                />
              </GridItem>
            ))}
          </Grid>
        </Box>

        <Box>
          <Text fontWeight="bold" fontSize="lg" mb={2}>
            Back side — more local coupons & branding slots
          </Text>

          <Grid
            templateColumns="repeat(auto-fit, minmax(170px, 1fr))"
            gap={1}
          >
            {right.map((slot) => (
              <GridItem key={slot.id} minW={0}>
                <AdvertSlotCard
                  slot={slot}
                  variant="public"
                  authBusiness={authBusiness}
                  authLoading={authLoading}
                />
              </GridItem>
            ))}
          </Grid>
        </Box>
      </Box>
    </Box>
  );
}
